<template>
  <!-- Food cost dots menu — contextual bottom sheet adapting to current page -->
  <v-bottom-sheet
    v-model="showMenuMenu"
    inset
    class="rounded"
    width="90%"
    v-if="$route.name !== 'NoAccess' && $route.name !== 'AccessDenied'"
  >
    <v-list density="comfortable">
      <v-toolbar density="compact" color="transparent">
        <v-toolbar-title>{{ isRecipesPage ? 'Recipes' : isIngredientsPage ? 'Ingredients' : isPackagingPage ? 'Packaging' : isCategoriesPage ? 'Categories' : isCombosPage ? 'Combos' : 'Food Cost' }}</v-toolbar-title>
        <template v-slot:append>
          <v-btn icon size="large" @click="showMenuMenu = false">
            <v-icon>mdi-close</v-icon>
            <v-tooltip activator="parent" location="bottom">Close</v-tooltip>
          </v-btn>
        </template>
      </v-toolbar>

      <v-divider></v-divider>

      <!-- Contextual admin actions -->
      <template v-if="isAdmin()">
        <!-- Recipes page: Add Recipe -->
        <v-list-item v-if="isRecipesPage" @click="onAddRecipe">
          <template v-slot:prepend>
            <v-icon icon="mdi-plus"></v-icon>
          </template>
          <template v-slot:title>Add Recipe</template>
          <template v-slot:subtitle>Create a new recipe</template>
        </v-list-item>

        <!-- (Ingredients are read-only — synced from accounting items — so no Add) -->

        <!-- Categories page: Add Category -->
        <v-list-item v-else-if="isCategoriesPage" @click="onAddCategory">
          <template v-slot:prepend>
            <v-icon icon="mdi-plus"></v-icon>
          </template>
          <template v-slot:title>Add Category</template>
          <template v-slot:subtitle>Create a new recipe category</template>
        </v-list-item>

        <!-- Combos page: Add Combo -->
        <v-list-item v-else-if="isCombosPage" @click="onAddCombo">
          <template v-slot:prepend>
            <v-icon icon="mdi-plus"></v-icon>
          </template>
          <template v-slot:title>Add Combo</template>
          <template v-slot:subtitle>Create a new combo meal</template>
        </v-list-item>

        <!-- Home page only: Add Menu Item (its create dialog lives on FoodCostHome,
             so don't offer it on ingredients/packaging/etc. where it'd no-op) -->
        <v-list-item v-else-if="isHomePage" @click="onAddMenuItem">
          <template v-slot:prepend>
            <v-icon icon="mdi-plus"></v-icon>
          </template>
          <template v-slot:title>Add Menu Item</template>
          <template v-slot:subtitle>Create a new menu item</template>
        </v-list-item>

        <!-- Ingredients page: Import -->
        <v-list-item v-if="isIngredientsPage" @click="onImportIngredients">
          <template v-slot:prepend>
            <v-icon icon="mdi-upload"></v-icon>
          </template>
          <template v-slot:title>Import Ingredients</template>
          <template v-slot:subtitle>Upload an Excel file</template>
        </v-list-item>
      </template>

      <!-- Menu items exports (all users, home page only) -->
      <v-list-item v-if="isHomePage" @click="onExportMenuItemsPdf">
        <template v-slot:prepend>
          <v-icon icon="mdi-file-pdf-box"></v-icon>
        </template>
        <template v-slot:title>Menu Items PDF</template>
        <template v-slot:subtitle>Export grouped by category with FC%</template>
      </v-list-item>
      <v-list-item v-if="isHomePage" @click="onExportMenuItemsCsv">
        <template v-slot:prepend>
          <v-icon icon="mdi-file-delimited-outline"></v-icon>
        </template>
        <template v-slot:title>Menu Items CSV</template>
        <template v-slot:subtitle>Export with selling prices and FC%</template>
      </v-list-item>

      <!-- Packaging list PDF (all users, packaging page only) -->
      <v-list-item v-if="isPackagingPage" @click="onExportPackagingPdf">
        <template v-slot:prepend>
          <v-icon icon="mdi-file-pdf-box"></v-icon>
        </template>
        <template v-slot:title>Packaging PDF</template>
        <template v-slot:subtitle>Export all packaging items as PDF</template>
      </v-list-item>

      <!-- Ingredients list PDF (all users, ingredients page only) -->
      <v-list-item v-if="isIngredientsPage" @click="onExportIngredientsPdf">
        <template v-slot:prepend>
          <v-icon icon="mdi-file-pdf-box"></v-icon>
        </template>
        <template v-slot:title>Ingredients PDF</template>
        <template v-slot:subtitle>Export all ingredients as PDF</template>
      </v-list-item>

      <!-- Recipes PDF export (all users, recipes page only) -->
      <v-list-item v-if="isRecipesPage" @click="onExportRecipesPdf">
        <template v-slot:prepend>
          <v-icon icon="mdi-file-pdf-box"></v-icon>
        </template>
        <template v-slot:title>Recipes PDF</template>
        <template v-slot:subtitle>Export all recipes as PDF</template>
      </v-list-item>

      <v-divider></v-divider>

      <!-- Navigation links — hide current page -->
      <v-list-item v-if="!isHomePage" @click="navigateTo('/menu')">
        <template v-slot:prepend>
          <v-icon icon="mdi-silverware-fork-knife" color="indigo"></v-icon>
        </template>
        <template v-slot:title>Menu Items</template>
        <template v-slot:subtitle>Food cost dashboard and menu items</template>
      </v-list-item>

      <v-list-item v-if="!isIngredientsPage" @click="navigateTo('/menu/ingredients')">
        <template v-slot:prepend>
          <v-icon icon="mdi-food-apple" color="green"></v-icon>
        </template>
        <template v-slot:title>Ingredients</template>
        <template v-slot:subtitle>Manage ingredient prices and categories</template>
      </v-list-item>

      <v-list-item v-if="!isRecipesPage" @click="navigateTo('/menu/recipes')">
        <template v-slot:prepend>
          <v-icon icon="mdi-file-document-outline" color="orange"></v-icon>
        </template>
        <template v-slot:title>Recipes</template>
        <template v-slot:subtitle>View recipes with cost analysis and margins</template>
      </v-list-item>

      <v-list-item v-if="!isPackagingPage" @click="navigateTo('/menu/packaging')">
        <template v-slot:prepend>
          <v-icon icon="mdi-package-variant-closed" color="blue"></v-icon>
        </template>
        <template v-slot:title>Packaging</template>
        <template v-slot:subtitle>Manage packaging items and prices</template>
      </v-list-item>

      <v-list-item v-if="!isCategoriesPage" @click="navigateTo('/menu/categories')">
        <template v-slot:prepend>
          <v-icon icon="mdi-tag-multiple" color="purple"></v-icon>
        </template>
        <template v-slot:title>Categories</template>
        <template v-slot:subtitle>Food cost by category, edit and assign recipes</template>
      </v-list-item>

      <v-list-item v-if="!isCombosPage" @click="navigateTo('/menu/combos')">
        <template v-slot:prepend>
          <v-icon icon="mdi-food-variant" color="teal"></v-icon>
        </template>
        <template v-slot:title>Combos</template>
        <template v-slot:subtitle>Meal deals and combo sets</template>
      </v-list-item>
      <v-divider class="my-1" />
      <v-list-item @click="goToItems">
        <template v-slot:prepend><v-icon icon="mdi-format-list-bulleted" color="green-darken-3"></v-icon></template>
        <template v-slot:title>Items</template>
        <template v-slot:subtitle>Manage master items catalog</template>
      </v-list-item>
    </v-list>
  </v-bottom-sheet>
</template>

<script setup>
import { computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useApp } from "@/composables/useApp.js";
import { useFoodCost } from "@/composables/useFoodCost.js";
import { generateRecipePdfReport } from "@/utils/recipePdfReport.js";
import { exportIngredientsListPdf, exportPackagingListPdf } from "@/utils/foodcostPdfExport.js";
import { generateMenuItemsPdfReport } from "@/utils/menuItemsPdfReport.js";

const router = useRouter();
const route = useRoute();
const { showMenuMenu } = useApp();
function goToItems() {
  showMenuMenu.value = false;
  router.push("/accounting/items");
}
const {
  openCreateMenuItem, openCreateRecipe, openCreateIngredient, openCreateCombo, isAdmin,
  recipes, allIngredients, ingredients, fetchRecipeDetail, fetchIngredients, fetchMenuItemDetail, showSnackbar,
  ingredientImportDialogOpen,
  categoryCreateDialogOpen,
  packagingItems,
  menuItems,
} = useFoodCost();

// ── Route detection for contextual menu ──────────────────────────────────
const isHomePage = computed(() => route.name === "MenuHome");
const isRecipesPage = computed(() => route.name === "MenuRecipes");
const isIngredientsPage = computed(() => route.name === "MenuIngredients");
const isPackagingPage = computed(() => route.name === "MenuPackaging");
const isCategoriesPage = computed(() => route.name === "MenuCategories");
const isCombosPage = computed(() => route.name === "MenuCombos");

/** Navigate to a route and close the menu */
function navigateTo(path) {
  showMenuMenu.value = false;
  router.push(path);
}

/** Open the add menu item dialog and close the menu */
function onAddMenuItem() {
  showMenuMenu.value = false;
  openCreateMenuItem();
}

/** Open the add recipe dialog and close the menu */
function onAddRecipe() {
  showMenuMenu.value = false;
  openCreateRecipe();
}

/** Open the add ingredient dialog and close the menu */
function onAddIngredient() {
  showMenuMenu.value = false;
  openCreateIngredient();
}

/** Open the import ingredients dialog and close the menu */
function onImportIngredients() {
  showMenuMenu.value = false;
  ingredientImportDialogOpen.value = true;
}

/** Open the add combo dialog and close the menu */
function onAddCombo() {
  showMenuMenu.value = false;
  openCreateCombo();
}

/** Open the add category dialog and close the menu */
function onAddCategory() {
  showMenuMenu.value = false;
  categoryCreateDialogOpen.value = true;
}

/** Export menu items as PDF grouped by brand with per-portion pricing */
async function onExportMenuItemsPdf() {
  showMenuMenu.value = false;
  try {
    // Fetch detail for each item to get selling_prices with portion_size
    const details = await Promise.all(
      menuItems.value.map((item) => fetchMenuItemDetail(item.id))
    );
    generateMenuItemsPdfReport({ data: details });
  } catch {
    showSnackbar("Failed to generate PDF", "error");
  }
}

/** CSV escape — wrap in quotes and escape internal quotes */
function csvEscape(val) {
  const str = String(val ?? "");
  return `"${str.replace(/"/g, '""')}"`;
}

/** Export menu items as platform-friendly CSV: name en/ar, desc en/ar, price, size, image */
async function onExportMenuItemsCsv() {
  showMenuMenu.value = false;
  try {
    // Fetch detail for each item to get selling_prices and images
    const details = await Promise.all(
      menuItems.value.map((item) => fetchMenuItemDetail(item.id))
    );
    const token = localStorage.getItem("authToken");
    const assetsBase = import.meta.env.VITE_API_ASSETS_URL;
    const rows = [["Name", "Name AR", "Description", "Description AR", "Size", "Brand", "Price", "Image Link"]];
    for (const item of details) {
      const prices = item.selling_prices?.length ? item.selling_prices : [{}];
      // First image (if any) — use as default image link
      const firstImg = item.images?.length ? item.images[0] : null;
      const imgLink = firstImg?.asset_id
        ? `${assetsBase}/api/assets/${firstImg.asset_id}/thumbnail?token=${token}`
        : "";
      for (const sp of prices) {
        rows.push([
          item.name || "",
          item.name_ar || "",
          item.description || "",
          item.description_ar || "",
          item.size || "",
          sp.brand_name || "",
          sp.price != null ? String(sp.price) : "",
          imgLink,
        ]);
      }
    }
    // Build CSV string
    const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "menu-items-platform.csv";
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    showSnackbar("Failed to export CSV", "error");
  }
}

/** Export all packaging items as a list PDF */
function onExportPackagingPdf() {
  showMenuMenu.value = false;
  exportPackagingListPdf(packagingItems.value);
}

/** Export all ingredients as a list PDF */
function onExportIngredientsPdf() {
  showMenuMenu.value = false;
  exportIngredientsListPdf(ingredients.value);
}

/** Export all recipes as PDF — fetches full detail for each recipe */
async function onExportRecipesPdf() {
  showMenuMenu.value = false;
  try {
    // Ensure ingredients are loaded for name lookup
    if (!ingredients.value.length) await fetchIngredients();

    // Fetch full detail (ingredients, steps, procedure) for every recipe
    const detailed = await Promise.all(
      recipes.value.map((r) => fetchRecipeDetail(r.id))
    );

    generateRecipePdfReport({ recipes: detailed, ingredients: allIngredients.value });
  } catch (err) {
    console.error("Failed to export recipes PDF:", err);
    showSnackbar("Failed to generate PDF", "error");
  }
}

</script>
